import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useBlocker } from "react-router-dom"
import { AlertTriangle, Info, Plus, Save, Upload, X } from "lucide-react"
import {
  useCommitLeaseIntake,
  useDiscardLeaseIntake,
  useUpdateLeaseIntake,
  type LeaseIntakeHeader,
  type LeaseIntakeHeaderKey,
  type LeaseIntakePatch,
  type LeaseIntakeRowField,
  type LeaseIntakeRowPatch,
  type LeaseIntakeRun,
} from "@/app/api/leaseIntakes"
import { ErrorBanner, Field, Input, formatApiError } from "@/app/components/FormPrimitives"
import type { ClientTheme } from "@/app/lib/themes"
import { stickyTh } from "@/app/lib/tableStyles"
import { compactButton, primaryButton, secondaryButton } from "@/app/lib/buttonStyles"
import { AMBER, Card, PanelLabel, headStyle, mutedText } from "@/app/pages/LeaseIntakePage/shared"
import { ROW_FIELDS, ReviewRow, type DraftRow } from "@/app/pages/LeaseIntakePage/ReviewRow"

// The UI warns when fewer rows came back than the survey estimated.
const LOW_YIELD_RATIO = 0.85

const HEADER_FIELDS: { key: LeaseIntakeHeaderKey; label: string }[] = [
  { key: "assignor", label: "Assignor" },
  { key: "assignee", label: "Assignee" },
  { key: "source_book", label: "Book" },
  { key: "source_page", label: "Page" },
  { key: "county", label: "County" },
  { key: "state", label: "State" },
  { key: "dated", label: "Dated" },
  { key: "effective", label: "Effective" },
  { key: "recorded", label: "Recorded" },
]

const ROW_FIELD_KEYS = ROW_FIELDS.map((f) => f.field)

// Local edit buffer over the server's staged rows. Edits accumulate here and
// go out in one PATCH on "Save changes" (or automatically before commit) —
// the same explicit-save pattern as the app's other edit screens.
interface Draft {
  runId: string
  header: LeaseIntakeHeader
  rows: DraftRow[]
  dirtyKeys: Set<string>
  deletedIds: string[]
  headerDirty: boolean
}

function buildDraft(run: LeaseIntakeRun): Draft {
  return {
    runId: run.id,
    header: { ...run.header },
    rows: [...run.rows].sort((a, b) => a.position - b.position).map((r) => ({ ...r, key: r.id })),
    dirtyKeys: new Set(),
    deletedIds: [],
    headerDirty: false,
  }
}

function isDirty(d: Draft): boolean {
  return d.headerDirty || d.dirtyKeys.size > 0 || d.deletedIds.length > 0
}

function buildPatch(d: Draft): LeaseIntakePatch {
  const patch: LeaseIntakePatch = {}
  if (d.headerDirty) patch.header = d.header
  const rows: LeaseIntakeRowPatch[] = []
  for (const r of d.rows) {
    if (!d.dirtyKeys.has(r.key)) continue
    const out: LeaseIntakeRowPatch = { include: r.include }
    if (r.id) out.id = r.id
    for (const f of ROW_FIELD_KEYS) out[f] = r[f]
    rows.push(out)
  }
  for (const id of d.deletedIds) rows.push({ id, _destroy: true })
  if (rows.length) patch.rows = rows
  return patch
}

let newRowSeq = 0

export function ReviewPanel({ run, theme }: { run: LeaseIntakeRun; theme: ClientTheme }) {
  const update = useUpdateLeaseIntake()
  const commit = useCommitLeaseIntake()
  const discard = useDiscardLeaseIntake()

  const [draft, setDraft] = useState<Draft>(() => buildDraft(run))
  // Adopt fresh server data (e.g. a background refetch) only while there are
  // no unsaved edits — never clobber the user's buffer.
  useEffect(() => {
    setDraft((prev) => (prev.runId === run.id && isDirty(prev) ? prev : buildDraft(run)))
  }, [run])

  const dirty = isDirty(draft)
  useUnsavedChangesGuard(dirty)

  const onField = useCallback((key: string, field: LeaseIntakeRowField, value: string) => {
    setDraft((d) => ({
      ...d,
      rows: d.rows.map((r) => (r.key === key ? { ...r, [field]: value } : r)),
      dirtyKeys: new Set(d.dirtyKeys).add(key),
    }))
  }, [])

  const onToggle = useCallback((key: string, include: boolean) => {
    setDraft((d) => ({
      ...d,
      rows: d.rows.map((r) => (r.key === key ? { ...r, include } : r)),
      dirtyKeys: new Set(d.dirtyKeys).add(key),
    }))
  }, [])

  const onDelete = useCallback((key: string) => {
    setDraft((d) => {
      const row = d.rows.find((r) => r.key === key)
      const dirtyKeys = new Set(d.dirtyKeys)
      dirtyKeys.delete(key)
      return {
        ...d,
        rows: d.rows.filter((r) => r.key !== key),
        dirtyKeys,
        deletedIds: row?.id ? [...d.deletedIds, row.id] : d.deletedIds,
      }
    })
  }, [])

  function addRow() {
    const key = `new-${++newRowSeq}`
    setDraft((d) => ({
      ...d,
      rows: [
        ...d.rows,
        {
          key,
          position: (d.rows[d.rows.length - 1]?.position ?? 0) + 1,
          include: true,
          lessor: "",
          lessee: "",
          lease_date: "",
          book: "",
          page: "",
          county: d.header.county ?? "",
          str: "",
          legal: "",
          flags: [],
          source_pages: null,
          on_file: false,
        },
      ],
      dirtyKeys: new Set(d.dirtyKeys).add(key),
    }))
  }

  function setHeader(key: LeaseIntakeHeaderKey, value: string) {
    setDraft((d) => ({ ...d, header: { ...d.header, [key]: value }, headerDirty: true }))
  }

  function fillBlankCounties(county: string) {
    const value = county.trim()
    if (!value) return
    setDraft((d) => {
      const dirtyKeys = new Set(d.dirtyKeys)
      const rows = d.rows.map((r) => {
        if ((r.county ?? "").trim()) return r
        dirtyKeys.add(r.key)
        return { ...r, county: value }
      })
      return { ...d, rows, dirtyKeys }
    })
  }

  async function save(): Promise<boolean> {
    if (!isDirty(draft)) return true
    try {
      const saved = await update.mutateAsync({ id: run.id, patch: buildPatch(draft) })
      setDraft(buildDraft(saved))
      return true
    } catch {
      return false // surfaced via update.isError
    }
  }

  async function handleCommit() {
    commit.reset()
    if (!(await save())) return
    commit.mutate(run.id)
  }

  const included = draft.rows.filter((r) => r.include)
  const missingCounty = included.filter((r) => !(r.county ?? "").trim()).length
  const blankCounties = draft.rows.filter((r) => !(r.county ?? "").trim()).length
  const estimated = run.estimated_leases ?? 0
  const lowYield = estimated > 0 && draft.rows.length < LOW_YIELD_RATIO * estimated
  const busy = update.isPending || commit.isPending || discard.isPending

  const commitBlocker =
    included.length === 0
      ? "Include at least one row to commit."
      : missingCounty > 0
        ? `${missingCounty} included row${missingCounty === 1 ? " is" : "s are"} missing a county — every lease on the schedule needs one.`
        : null

  const rowElements = useMemo(
    () =>
      draft.rows.map((row, i) => (
        <ReviewRow
          key={row.key}
          row={row}
          index={i}
          onField={onField}
          onToggle={onToggle}
          onDelete={onDelete}
          theme={theme}
        />
      )),
    [draft.rows, onField, onToggle, onDelete, theme]
  )

  return (
    <div className="flex flex-col gap-4">
      {lowYield && (
        <Banner theme={theme} color={AMBER} icon={<AlertTriangle size={15} />}>
          Found {draft.rows.length} lease{draft.rows.length === 1 ? "" : "s"}, but the document
          looked like it holds about {estimated}. Some entries may have been missed — compare
          against the document before committing.
        </Banner>
      )}

      {run.notes.length > 0 && (
        <Banner theme={theme} color={theme.brandPrimary} icon={<Info size={15} />}>
          <div className="flex flex-col gap-1">
            <strong style={{ color: theme.textPrimary }}>Notes from extraction</strong>
            <ul className="list-disc pl-4 flex flex-col gap-0.5">
              {run.notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </div>
        </Banner>
      )}

      <Card theme={theme}>
        <PanelLabel theme={theme}>Source document</PanelLabel>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {HEADER_FIELDS.map(({ key, label }) => (
            <Field key={key} label={label} theme={theme}>
              <Input
                value={draft.header[key] ?? ""}
                onChange={(v) => setHeader(key, v)}
                theme={theme}
              />
            </Field>
          ))}
        </div>
        <div style={{ ...mutedText(theme, "11px"), color: theme.textDim, marginTop: "10px" }}>
          Book/page, parties, and recording date become each committed lease's source. Leave book
          and page blank for a standalone lease or CSV.
        </div>
      </Card>

      <div
        className="rounded-lg border overflow-hidden"
        style={{ borderColor: theme.borderColor, backgroundColor: theme.bgSurface }}
      >
        <div
          className="flex flex-wrap items-center gap-3 px-4 py-3"
          style={{ borderBottom: `1px solid ${theme.borderColor}` }}
        >
          <span style={mutedText(theme)}>
            <strong style={{ color: theme.textPrimary }}>{included.length}</strong> of{" "}
            {draft.rows.length} row{draft.rows.length === 1 ? "" : "s"} included
          </span>
          <div className="flex-1" />
          <FillCountiesControl
            key={draft.header.county ?? ""}
            defaultCounty={draft.header.county ?? ""}
            blankCount={blankCounties}
            onFill={fillBlankCounties}
            theme={theme}
          />
          <button type="button" onClick={addRow} className="btn" style={compactButton(theme)}>
            <Plus size={13} /> Add row
          </button>
        </div>

        <div className="overflow-auto" style={{ maxHeight: "65vh" }}>
          <table className="border-collapse" style={{ minWidth: "100%" }}>
            <thead>
              <tr>
                <th
                  className="pl-3 pr-1 py-2 text-left"
                  style={{ ...stickyTh(theme), ...headStyle(theme) }}
                >
                  Incl.
                </th>
                <th
                  className="px-1 py-2 text-left"
                  style={{ ...stickyTh(theme), ...headStyle(theme) }}
                >
                  #
                </th>
                {ROW_FIELDS.map(({ field, label }) => (
                  <th
                    key={field}
                    className="px-1 py-2 text-left"
                    style={{ ...stickyTh(theme), ...headStyle(theme) }}
                  >
                    {label}
                    {field === "county" && <span style={{ color: theme.brandAccent }}> *</span>}
                  </th>
                ))}
                <th
                  className="px-1 py-2 text-left"
                  style={{ ...stickyTh(theme), ...headStyle(theme) }}
                >
                  Flags
                </th>
                <th
                  className="px-1 py-2 text-left"
                  style={{ ...stickyTh(theme), ...headStyle(theme) }}
                >
                  Pages
                </th>
                <th className="pl-1 pr-3 py-2" style={stickyTh(theme)} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {draft.rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={ROW_FIELDS.length + 5}
                    className="px-5 py-10 text-center"
                    style={mutedText(theme, "13px")}
                  >
                    No leases were found. Add rows by hand, or discard and try another file.
                  </td>
                </tr>
              ) : (
                rowElements
              )}
            </tbody>
          </table>
        </div>
      </div>

      {update.isError && <ErrorBanner theme={theme} message={formatApiError(update.error)} />}
      {commit.isError && <ErrorBanner theme={theme} message={formatApiError(commit.error)} />}
      {discard.isError && <ErrorBanner theme={theme} message={formatApiError(discard.error)} />}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleCommit}
          disabled={!!commitBlocker || busy}
          className="btn"
          style={{
            ...primaryButton(theme),
            opacity: commitBlocker || busy ? 0.6 : 1,
            cursor: commitBlocker || busy ? "default" : "pointer",
          }}
          title={commitBlocker ?? "Save any edits and add the included rows to the Lease Schedule"}
        >
          <Upload size={14} />
          {commit.isPending
            ? "Committing…"
            : `Commit ${included.length} lease${included.length === 1 ? "" : "s"}`}
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!dirty || busy}
          className="btn"
          style={{
            ...secondaryButton(theme),
            opacity: !dirty || busy ? 0.6 : 1,
            cursor: !dirty || busy ? "default" : "pointer",
          }}
        >
          <Save size={14} /> {update.isPending ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
        {commitBlocker && (
          <span style={{ ...mutedText(theme), color: theme.brandAccent }}>{commitBlocker}</span>
        )}
        <div className="flex-1" />
        <DiscardControl
          onDiscard={() => discard.mutate(run.id)}
          disabled={busy}
          pending={discard.isPending}
          theme={theme}
        />
      </div>
    </div>
  )
}

function FillCountiesControl({
  defaultCounty,
  blankCount,
  onFill,
  theme,
}: {
  defaultCounty: string
  blankCount: number
  onFill: (county: string) => void
  theme: ClientTheme
}) {
  const [county, setCounty] = useState(defaultCounty)
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={county}
        onChange={(e) => setCounty(e.target.value)}
        placeholder="County"
        aria-label="County to fill into blank rows"
        style={{
          width: "130px",
          padding: "5px 9px",
          borderRadius: "8px",
          backgroundColor: theme.bgSurface2,
          border: `1px solid ${theme.borderColor}`,
          color: theme.textPrimary,
          fontFamily: "var(--font-ui)",
          fontSize: "12px",
          outline: "none",
        }}
      />
      <button
        type="button"
        onClick={() => onFill(county)}
        disabled={!county.trim() || blankCount === 0}
        className="btn"
        style={{
          ...compactButton(theme),
          opacity: !county.trim() || blankCount === 0 ? 0.6 : 1,
          cursor: !county.trim() || blankCount === 0 ? "default" : "pointer",
        }}
        title={blankCount === 0 ? "Every row already has a county" : undefined}
      >
        Fill blank counties ({blankCount})
      </button>
    </div>
  )
}

function DiscardControl({
  onDiscard,
  disabled,
  pending,
  theme,
}: {
  onDiscard: () => void
  disabled: boolean
  pending: boolean
  theme: ClientTheme
}) {
  const [confirming, setConfirming] = useState(false)
  if (confirming) {
    return (
      <span className="flex items-center gap-2">
        <span style={mutedText(theme)}>Discard this import? Staged rows will be dropped.</span>
        <button
          type="button"
          onClick={onDiscard}
          disabled={disabled}
          className="btn"
          style={{ ...compactButton(theme), color: theme.brandAccent }}
        >
          {pending ? "Discarding…" : "Discard"}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="btn"
          style={compactButton(theme)}
        >
          Cancel
        </button>
      </span>
    )
  }
  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      disabled={disabled}
      className="btn"
      style={{ ...compactButton(theme), color: theme.brandAccent }}
    >
      <X size={13} /> Discard import
    </button>
  )
}

function Banner({
  theme,
  color,
  icon,
  children,
}: {
  theme: ClientTheme
  color: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div
      className="rounded-lg p-3 flex items-start gap-2"
      style={{ backgroundColor: `${color}0F`, border: `1px solid ${color}40` }}
    >
      <span style={{ color, marginTop: "1px", flexShrink: 0, display: "flex" }}>{icon}</span>
      <div style={{ ...mutedText(theme), lineHeight: 1.5 }}>{children}</div>
    </div>
  )
}

// Unsaved review edits: the browser's "leave site?" prompt for reloads/tab
// closes, plus a confirm before any in-app navigation (sidebar, back link) —
// the app uses a data router (createBrowserRouter), so useBlocker applies.
function useUnsavedChangesGuard(dirty: boolean) {
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname
  )
  useEffect(() => {
    if (blocker.state !== "blocked") return
    if (window.confirm("You have unsaved edits to this import. Leave without saving?")) {
      blocker.proceed()
    } else {
      blocker.reset()
    }
  }, [blocker])
}
